// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface CsvUploadProps {
  uploadErrorMessage: string | null;
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadLineCount?: number;
}

const ESTIMATED_RECORDS_PER_SECOND = 20;

export function CsvUpload({
  uploadErrorMessage,
  file,
  isUploading,
  onSelectFile,
  onUpload,
  onBack,
  onClose,
  hasFatalError,
  uploadLineCount,
}: CsvUploadProps) {
  const estimatedUploadTime = uploadLineCount
    ? Math.round(uploadLineCount / ESTIMATED_RECORDS_PER_SECOND) + 2
    : 0;

  // State for countdown timer
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle upload with countdown
  const handleUpload = () => {
    // Call the original upload function
    onUpload();
    // Start countdown
    setCountdown(estimatedUploadTime);
  };

  // Effect to manage countdown timer
  useEffect(() => {
    // If uploading and we don't have a timer yet, start one
    if (isUploading && countdown > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    // If not uploading, clear the timer and reset countdown
    if (!isUploading) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        // Only clear the countdown if the timer ref is active
        setCountdown(0);
      }
    }

    // Cleanup timer when component unmounts
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isUploading, countdown]);

  return (
    <Card>
      <CardContent>
        {uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadErrorMessage}
          </Alert>
        )}

        {hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.50',
                },
              }}
              onClick={onSelectFile}
            >
              {file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {file ? file.name : 'Upload a CSV file with your time data'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {file
                  ? `Ready to upload ${formatNumber(uploadLineCount ?? 0)} time records (${formatNumber(Math.round(file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Warning about time data imports</AlertTitle>
              <ul
                style={{
                  paddingLeft: '1.5rem',
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <li>
                  All required fields (Date, Duration, Category, Worker) must be
                  included
                </li>
                <li>
                  Worker must match an existing user's name or email address
                </li>
                <li>
                  Project must match an existing project name (if provided)
                </li>
                <li>
                  Duration can be in formats like '1.5h', '90m', '1h 30m', or
                  '1:30'
                </li>
              </ul>
            </Alert>
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent={'center'}
        >
          <Button variant="outlined" onClick={onBack} disabled={isUploading}>
            Back
          </Button>
          <Box display="flex" alignItems={'center'}>
            {uploadLineCount !== undefined && !isUploading && (
              <Typography color={'text.secondary'}>
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {isUploading && countdown > 0 && (
              <Typography color={'text.secondary'}>
                Estimated time remaining: {formatDuration(countdown)}
              </Typography>
            )}
          </Box>
          {hasFatalError && onClose ? (
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                isUploading ? <CircularProgress size={20} /> : <Upload />
              }
              onClick={handleUpload}
              disabled={!file || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
